import React, { useEffect, useMemo, useState } from "react";
import styled, { keyframes } from "styled-components";
import { FaTimes, FaCheck } from "react-icons/fa";
import { IoMdArrowRoundForward } from "react-icons/io";
import { useAdoptModal } from "../context/AdoptModalContext";
import {
  CREATE_CHECKOUT_URL,
  PRICE_PER_DUCK_RON,
  STRIPE_BUY_URL,
} from "../data/EventConfig";

const fadeIn = keyframes`
  from { opacity: 0; }
  to { opacity: 1; }
`;
const slideUp = keyframes`
  from { opacity: 0; transform: translateY(20px) scale(0.98); }
  to { opacity: 1; transform: translateY(0) scale(1); }
`;

const Backdrop = styled.div`
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(10, 42, 74, 0.6);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 1rem;
  animation: ${fadeIn} 0.2s ease;
`;

const Card = styled.div`
  background: #fff;
  border-radius: 20px;
  width: 100%;
  max-width: 480px;
  box-shadow: 0 24px 80px rgba(0, 0, 0, 0.35);
  animation: ${slideUp} 0.28s cubic-bezier(0.22, 0.61, 0.36, 1);
  overflow: hidden;
  display: flex;
  flex-direction: column;
  max-height: calc(100vh - 2rem);
`;

const Header = styled.div`
  background: linear-gradient(135deg, #ffce2d 0%, #faa71e 100%);
  padding: 1.5rem 1.5rem 1.25rem;
  position: relative;
`;

const Title = styled.h2`
  color: #0a2a4a;
  font-size: 1.4rem;
  font-weight: 800;
  margin: 0 0 0.35rem;
`;

const Sub = styled.p`
  color: rgba(10, 42, 74, 0.78);
  font-size: 0.92rem;
  margin: 0;
`;

const CloseBtn = styled.button`
  position: absolute;
  top: 14px;
  right: 14px;
  width: 36px;
  height: 36px;
  border: none;
  border-radius: 50%;
  background: rgba(10, 42, 74, 0.12);
  color: #0a2a4a;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease;

  &:hover {
    background: rgba(10, 42, 74, 0.22);
  }
`;

const Body = styled.div`
  padding: 1.5rem;
  overflow-y: auto;
`;

const Label = styled.label`
  display: block;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.1em;
  text-transform: uppercase;
  color: #0a2a4a;
  margin-bottom: 0.5rem;
`;

const QtyRow = styled.div`
  display: flex;
  align-items: stretch;
  gap: 0.5rem;
  margin-bottom: 1rem;
`;

const QtyBtn = styled.button`
  width: 44px;
  height: 44px;
  border: 1px solid rgba(10, 42, 74, 0.18);
  background: #fff;
  color: #0a2a4a;
  border-radius: 12px;
  font-size: 1.4rem;
  font-weight: 700;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover:not(:disabled) {
    background: #ffce2d;
    border-color: #ffce2d;
  }

  &:disabled {
    opacity: 0.4;
    cursor: not-allowed;
  }
`;

const QtyInput = styled.input`
  flex: 1;
  height: 44px;
  border: 1px solid rgba(10, 42, 74, 0.18);
  border-radius: 12px;
  text-align: center;
  font-size: 1.15rem;
  font-weight: 700;
  color: #0a2a4a;
  outline: none;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: #faa71e;
  }
`;

const Presets = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 0.5rem;
  margin-bottom: 1.25rem;
`;

const Preset = styled.button`
  border: 1px solid rgba(10, 42, 74, 0.14);
  background: ${({ $active }) => ($active ? "#0a2a4a" : "#fff")};
  color: ${({ $active }) => ($active ? "#ffce2d" : "#0a2a4a")};
  padding: 0.6rem 0;
  border-radius: 10px;
  font-weight: 700;
  font-size: 0.9rem;
  cursor: pointer;
  transition: all 0.15s ease;

  &:hover {
    border-color: #faa71e;
  }
`;

const Field = styled.input`
  width: 100%;
  height: 44px;
  border: 1px solid rgba(10, 42, 74, 0.18);
  border-radius: 12px;
  padding: 0 0.9rem;
  font-size: 0.95rem;
  color: #0a2a4a;
  outline: none;
  margin-bottom: 0.85rem;
  transition: border-color 0.15s ease;

  &:focus {
    border-color: #faa71e;
  }
`;

const Summary = styled.div`
  background: #fff8e1;
  border: 1px solid rgba(255, 206, 45, 0.5);
  border-radius: 14px;
  padding: 1rem 1.1rem;
  margin: 0.5rem 0 1.25rem;
`;

const Row = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 0.92rem;
  color: #0a2a4a;
  padding: 0.25rem 0;

  strong { font-weight: 800; }
`;

const Bonus = styled(Row)`
  color: #1a7a3a;

  svg { flex-shrink: 0; }
`;

const Total = styled(Row)`
  border-top: 1px dashed rgba(10, 42, 74, 0.2);
  margin-top: 0.5rem;
  padding-top: 0.6rem;
  font-size: 1.05rem;
  font-weight: 800;
`;

const Submit = styled.button`
  width: 100%;
  height: 52px;
  border: none;
  border-radius: 999px;
  background: #faa71e;
  color: #fff;
  font-size: 1rem;
  font-weight: 800;
  letter-spacing: 0.02em;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  box-shadow: 0 12px 24px rgba(250, 167, 30, 0.35);
  transition: transform 0.15s ease, box-shadow 0.15s ease;

  &:hover:not(:disabled) {
    transform: translateY(-1px);
    box-shadow: 0 16px 28px rgba(250, 167, 30, 0.45);
  }

  &:disabled {
    opacity: 0.65;
    cursor: not-allowed;
  }
`;

const ErrorBox = styled.div`
  background: rgba(220, 38, 38, 0.08);
  border: 1px solid rgba(220, 38, 38, 0.3);
  color: #b91c1c;
  font-size: 0.86rem;
  padding: 0.6rem 0.85rem;
  border-radius: 10px;
  margin-bottom: 0.85rem;
`;

const Note = styled.p`
  font-size: 0.78rem;
  color: rgba(10, 42, 74, 0.6);
  text-align: center;
  margin: 0.75rem 0 0;
`;

const presets = [1, 5, 10, 20];
const MIN_QTY = 1;
const MAX_QTY = 200;

function bonusFor(q) {
  return Math.floor(q / 10);
}

const AdoptModal = () => {
  const { isOpen, close } = useAdoptModal();
  const [qty, setQty] = useState(10);
  const [email, setEmail] = useState("");
  const [name, setName] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!isOpen) {
      setError("");
      setSubmitting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    if (!isOpen) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") close();
    };
    document.body.style.overflow = "hidden";
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [isOpen, close]);

  const bonus = useMemo(() => bonusFor(qty), [qty]);
  const total = qty + bonus;
  const priceRon = qty * PRICE_PER_DUCK_RON;

  if (!isOpen) return null;

  const setQtyClamped = (v) => {
    const n = Number(v);
    if (!Number.isFinite(n)) return;
    setQty(Math.max(MIN_QTY, Math.min(MAX_QTY, Math.floor(n))));
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");

    if (!CREATE_CHECKOUT_URL) {
      // Backend not configured yet — fall back to the static buy link.
      window.open(STRIPE_BUY_URL, "_blank", "noopener,noreferrer");
      return;
    }

    setSubmitting(true);
    try {
      const resp = await fetch(CREATE_CHECKOUT_URL, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          paidQty: qty,
          email,
          name,
          origin: window.location.origin,
        }),
      });
      const data = await resp.json();
      if (!resp.ok || !data.url) {
        throw new Error(data.error || "Nu am putut iniția plata.");
      }
      window.location.href = data.url;
    } catch (err) {
      setError(err.message || "A apărut o eroare. Încearcă din nou.");
      setSubmitting(false);
    }
  };

  return (
    <Backdrop onClick={close}>
      <Card onClick={(e) => e.stopPropagation()}>
        <Header>
          <Title>Adoptă o rățușcă</Title>
          <Sub>10 RON / rățușcă · primești 1 cadou la fiecare 10 cumpărate</Sub>
          <CloseBtn onClick={close} aria-label="Închide">
            <FaTimes size={14} />
          </CloseBtn>
        </Header>

        <Body>
          <form onSubmit={submit}>
            <Label htmlFor="qty">Numărul de rățuște</Label>
            <QtyRow>
              <QtyBtn
                type="button"
                onClick={() => setQtyClamped(qty - 1)}
                disabled={qty <= MIN_QTY}
                aria-label="Scade"
              >
                −
              </QtyBtn>
              <QtyInput
                id="qty"
                type="number"
                min={MIN_QTY}
                max={MAX_QTY}
                value={qty}
                onChange={(e) => setQtyClamped(e.target.value)}
              />
              <QtyBtn
                type="button"
                onClick={() => setQtyClamped(qty + 1)}
                disabled={qty >= MAX_QTY}
                aria-label="Crește"
              >
                +
              </QtyBtn>
            </QtyRow>

            <Presets>
              {presets.map((p) => (
                <Preset
                  key={p}
                  type="button"
                  $active={qty === p}
                  onClick={() => setQty(p)}
                >
                  {p}
                </Preset>
              ))}
            </Presets>

            <Label htmlFor="name">Nume (opțional)</Label>
            <Field
              id="name"
              type="text"
              placeholder="Numele tău"
              value={name}
              onChange={(e) => setName(e.target.value)}
              autoComplete="name"
            />
            <Label htmlFor="email">Email pentru bilete</Label>
            <Field
              id="email"
              type="email"
              placeholder="email@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              autoComplete="email"
            />

            <Summary>
              <Row>
                <span>Rățuște plătite</span>
                <strong>{qty}</strong>
              </Row>
              {bonus > 0 && (
                <Bonus>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
                    <FaCheck size={12} /> Cadou
                  </span>
                  <strong>+{bonus}</strong>
                </Bonus>
              )}
              <Total>
                <span>Total bilete · {priceRon} RON</span>
                <strong>{total}</strong>
              </Total>
            </Summary>

            {error && <ErrorBox>{error}</ErrorBox>}

            <Submit type="submit" disabled={submitting}>
              {submitting ? "Se redirecționează..." : "Plătește în siguranță"}
              <IoMdArrowRoundForward size={18} />
            </Submit>

            <Note>Plata se face prin Stripe. Biletele sosesc pe email.</Note>
          </form>
        </Body>
      </Card>
    </Backdrop>
  );
};

export default AdoptModal;
